import classnames from 'classnames';
import { connect } from 'react-redux';
import autobind from 'core-decorators/lib/autobind';
import ui from 'redux-ui';
import { FormattedMessage } from 'react-intl';

import { getNewRelic } from 'shared/analytics/new-relic';

import { WixConnect } from 'shared/utils/wix-connect';
import memoizedPartial from 'shared/utils/memoized-partial';
import { openMobileOverlay } from 'shared/utils/open-overlay';
import { playerSizeMobile, playerSizeMobileMainItemForNewUX } from 'widget/utils/videos-sizes';
import { getVideoSelectedEventParams } from 'shared/utils/bi/video-overlay-events-params';
import { storeForReload } from 'widget/utils/reload';

import { canPlayFullOrPreview } from 'widget/ui-selectors/video-access';
import { getAllSettings } from 'shared/selectors/app-settings';
import { getVideosGroupedByIds } from 'shared/selectors/videos';
import { getMainVideoId } from 'widget/ui-selectors/selected-video-id';
import { isVideoPlaying } from 'widget/selectors/video-playback-status';
import { shouldRenderRealPlayer } from 'widget/ui-selectors/mobile/should-render-real-player';

import { openVideoPage } from 'widget/redux/client/actions/mobile-overlay-openners/main-openners';
import { selectVideo, resetSelectedVideo } from 'widget/redux/client/actions/select-video';
import { playVideo, pauseVideo, resetPlaybackStatus } from 'widget/redux/client/actions/change-playback-status';

import { MOBILE_WIDGET_DATA_KEY } from 'widget/data-components/lazy-channel-videos';
import BaseView from 'widget/views/_base';

import MobilePlayer from 'widget/components/players/mobile-player';
import MainWidgetMobilePlayer from 'widget/components/players/main-widget-mobile-player';
import OverlayMainVideoMobile from 'widget/components/player-overlay/mobile/main-video/main-video';
import SlidableListWithLazyLoad from 'widget/components/slidable-list-with-lazy-load/slidable-list-with-lazy-load';
import { LinkButton } from 'shared/components/button/button';
import Icon from 'shared/components/icon/icon';
import Header from 'widget/components/header-mobile/header-mobile';
import VideosListMobile from 'widget/components/videos-list-mobile/videos-list-mobile';
import PaymentEvents from 'shared/components/payment-events/payment-events';
import ReloadStorage from 'widget/components/reload-storage/reload-storage';
import CoverWithOverlay from 'widget/components/cover-with-overlay/mobile-cover-with-overlay';

import styles from './mobile.styl';

import translations from './translations';
import widgetTranslations from 'widget/translations';

import paymentsHelper from 'widget/utils/payments';
import { isNewMobileUXEnabled } from 'shared/selectors/experiments';


@connect(state => ({
  isVideoPlaying: isVideoPlaying(state),
  currentChannelId: state.currentChannelId,
  canShowChannelCover: !state.isVideosTouched,
  appSettings: getAllSettings(state),
  videoByIds: getVideosGroupedByIds(state),
  firstChannelVideoId: state.firstChannelVideoId,
  newMobileUXIsEnabled: isNewMobileUXEnabled(state),
  videoIds: state.lazyVideoLists.mobileWidget.videoIds,
  mainVideoId: getMainVideoId(state, MOBILE_WIDGET_DATA_KEY),
  shouldRenderRealPlayer: shouldRenderRealPlayer(state, MOBILE_WIDGET_DATA_KEY),
}), {
  openVideoPage,
  selectVideo,
  resetSelectedVideo,
  playVideo,
  pauseVideo,
  resetPlaybackStatus,
})
@ui()
@WixConnect(styles)
export default class MobileView extends BaseView {

  static displayName = 'MobileView';

  static propTypes = {
    resetPlaybackStatus: React.PropTypes.func,
    lazyChannelVideos: React.PropTypes.object,
    newMobileUXIsEnabled: React.PropTypes.bool,
    openVideoPage: React.PropTypes.func,
    shouldRenderRealPlayer: React.PropTypes.bool,
  };

  constructor(props) {
    super(props);

    getNewRelic().setCustomAttribute('layout', 'mobile');
  }

  get mainVideoList() {
    return this.props.lazyChannelVideos;
  }

  get playerSize() {
    const { newMobileUXIsEnabled, appSettings } = this.props;
    const widgetWidth = appSettings.requestedWidgetSize.width;

    return newMobileUXIsEnabled ?
      playerSizeMobileMainItemForNewUX(widgetWidth) :
      playerSizeMobile(widgetWidth);
  }

  get isFetching() {
    const { mainVideoList } = this;
    const { channel } = this.props;
    return channel.isFetching || mainVideoList.isFetching && !_.get(mainVideoList, 'ids.length');
  }

  @autobind
  processSubscriptionCancel() {
    const { appSettings: { siteUrl, instance }, currentChannelId } = this.props;

    paymentsHelper.cancelSubscription(currentChannelId, siteUrl, instance);
  }

  @autobind
  openMemberOnlyPage(id) {
    openMobileOverlay(`#/membership/${id}`);
  }

  @autobind
  processDataFromStorage({ selectedVideoId }) {
    if (selectedVideoId) {
      this.selectVideo({ id: selectedVideoId });
    }
  }

  @autobind
  storeDataForReload() {
    storeForReload({
      selectedVideoId: this.props.mainVideoId,
    });
  }

  get player() {
    const videoItem = this.mainVideo;
    const playerOverlay = this.playerOverlay;
    const { width, height } = this.playerSize;

    if (!videoItem) {
      return this.noVideosPlayerContent;
    }

    return (
      <MobilePlayer styleName="player"
                    videoItem={videoItem}
                    channel={this.props.channelData}
                    width={width}
                    height={height}
                    canShowChannelCover={this.canShowChannelCover}
                    onPurchaseClick={this.openPurchase}
                    onSubscribeClick={this.openSubscription}
                    onMemberSignUp={memoizedPartial(this.openMemberOnlyPage, videoItem.id)}>
        {playerOverlay}
      </MobilePlayer>
    );
  }

  @autobind
  playSelectedVideo() {
    const { channelData, currentSiteUser, selectVideo, playVideo } = this.props;

    const videoItem = this.mainVideo;
    const isPlayAllowed = canPlayFullOrPreview(channelData, videoItem, currentSiteUser.data);

    if (isPlayAllowed) {
      playVideo(videoItem.id);
    } else {
      selectVideo(videoItem.id);
    }
  }

  @autobind
  onCurrentSlideChanged(newIndex) {
    const { videoIds } = this.props;
    this.selectVideo({ id: videoIds[newIndex] });
  }

  get noVideoPlayerOverlay() {
    const { appSettings, channelData } = this.props;

    const props = {
      appSettings,
      channelData,
      showChannelCover: true,
      videoItem: {},
    };

    return (
      <OverlayMainVideoMobile {...props} />
    );
  }

  get playerOverlay() {
    const videoItem = this.mainVideo;
    const openVideoPurchasePage = memoizedPartial(openMobileOverlay, `#/video/${videoItem.id}/purchase`);
    const openSubscriptionPage = memoizedPartial(openMobileOverlay, '#/channel/subscribe');

    return (
      <OverlayMainVideoMobile onPlayClick={this.playSelectedVideo}
                              onPurchaseClick={openVideoPurchasePage}
                              onSubscribeClick={openSubscriptionPage}
                              onTitleClick={openMobileOverlay} />
    );
  }

  clearStoredDataForMobileOverlay() {
    storeForReload({
      selectedVideoId: null,
    });
  }

  @autobind
  handleExploreChannelClick() {
    this.sendExploreThisChannelEvent();
    this.clearStoredDataForMobileOverlay();
    openMobileOverlay();
  }

  get footer() {
    if (this.props.channelData.videosCount === 0) {
      return (
        <div styleName="footer coming-soon"
             key="footer">
          <FormattedMessage {...widgetTranslations.thisChannelIsComingSoon} />
        </div>
      );
    }

    return (
      <div styleName="footer"
           key="footer">
        <LinkButton styleName="explore-channel"
                    onClick={this.handleExploreChannelClick}>
          <FormattedMessage {...translations.exploreThisChannel} />
          <Icon styleName="search-icon"
                name="permit-search" />
        </LinkButton>
      </div>
    );
  }

  sendExploreThisChannelEvent() {
    this.props.biEvent('widget.exploreThisChannel.clicked');
  }

  @autobind
  onVideoItemClicked(videoItemId) {
    this.sendVidSelectedBiEvent(videoItemId);
    this.props.selectVideo(videoItemId);
  }

  get carousel() {
    const { mainVideoId, currentChannelId } = this.props;
    const { loadMore } = this.mainVideoList;

    if (this.isFetching || !this.mainVideo) {
      return null;
    }

    return (
      <VideosListMobile key="carousel"
                        styleName="videos-carousel"
                        currentChannelId={currentChannelId}
                        selectedVideoId={mainVideoId}
                        onVideoItemClicked={this.onVideoItemClicked}
                        loadMore={loadMore} />
    );
  }

  sendVidSelectedBiEvent(newVideoId) {
    const { appSettings, channelData, videoByIds } = this.props;
    this.props.biEvent(
      'widget.VidSelected.displayed',
      getVideoSelectedEventParams(appSettings, channelData, videoByIds[newVideoId], this.canShowChannelCover),
    );
  }

  @autobind
  onCoverPlayClickInNewUI() {
    // TODO: move to action
    const { shouldRenderRealPlayer, openVideoPage, mainVideoId } = this.props;
    if (shouldRenderRealPlayer) {
      this.playSelectedVideo();
    } else {
      openVideoPage(mainVideoId);
    }
  }

  renderCoverForCarousel(videoItem, index, className) {
    const { width, height } = this.playerSize;
    const featuredVideoId = _.get(this.props.channelData, 'featuredItem');
    const isInitialVideo = featuredVideoId ? featuredVideoId === videoItem.id : index === 0;
    const showChannelCover = this.canShowChannelCover && isInitialVideo;

    return (
      <CoverWithOverlay videoItem={videoItem}
                        className={className}
                        showChannelCover={showChannelCover}
                        itemWidth={width}
                        itemHeight={height}
                        onPlayClick={this.onCoverPlayClickInNewUI}
                        onMemberSignUp={memoizedPartial(this.openMemberOnlyPage, videoItem.id)} />
    );
  }

  get coversListForCarousel() {
    const { videoIds, videoByIds } = this.props;

    return _.map(videoIds, (videoId, index) => (
      <div key={videoId}>
        {this.renderCoverForCarousel(videoByIds[videoId], index, styles['overlay-item-topmost'])}
      </div>
    ));
  }

  get coversList() {
    const { loadMore } = this.mainVideoList;
    const { videoIds } = this.props;
    const videoItem = this.mainVideo;

    if (_.size(videoIds) === 1) {
      return this.renderCoverForCarousel(videoItem);
    }

    return (
      <SlidableListWithLazyLoad loadMore={loadMore}
                                styleName="players-list-slidable"
                                onCurrentSlideChanged={this.onCurrentSlideChanged}
                                selectedVideoId={videoItem && videoItem.id}>
        {this.coversListForCarousel}
      </SlidableListWithLazyLoad>
    );
  }

  get newMobileUI() {
    const { isVideoPlaying, channelData, shouldRenderRealPlayer } = this.props;
    const { width, height } = this.playerSize;
    const videoItem = this.mainVideo;

    if (!videoItem) {
      return this.noVideosPlayerContent;
    }

    return (
      <div styleName="new-mobile-ui-container"
           style={{ width, height }}>
        <div styleName="real-player-placeholder">
          {shouldRenderRealPlayer ?
            (<MainWidgetMobilePlayer videoItem={videoItem}
                                     channelData={channelData}
                                     width={width}
                                     height={height}
                                     showPoster={false}
                                     paused={!isVideoPlaying}
                                     onMemberSignUp={this.openMemberOnlyPage} />) :
            null}
        </div>
        {this.coversList}
      </div>
    );
  }

  get mainUI() {
    return [
      <Header styleName="header"
              key="header"
              channel={this.props.channelData}
              onUserLogin={this.storeDataForReload}
              onUserLogout={this.storeDataForReload}
              onSubscribeClick={memoizedPartial(openMobileOverlay, '#/channel/subscribe')}
              onSubscriptionCancelClick={this.processSubscriptionCancel} />,
      <div styleName="player-wrapper"
           key="wrapper">
        {this.player}
      </div>,
      this.carousel,
      this.footer,
    ];
  }

  get isVideosLoaded() {
    const { videoIds, lazyChannelVideos } = this.props;
    return !lazyChannelVideos.isFetching || !_.isEmpty(videoIds);
  }

  render() {
    if (!this.isUIBlockingDataLoaded || !this.isVideosLoaded) {
      return this.spinner;
    }

    const { appSettings, newMobileUXIsEnabled } = this.props;

    const styleNames = classnames('container', `view-mode-${appSettings.editMode}`);

    return (
      <section styleName={styleNames}
               className="qa-widget-container">
        <ReloadStorage onReload={this.processDataFromStorage} />
        <PaymentEvents onSubscription={this.reset}
                       onSubscriptionCancel={this.reset} />

        { newMobileUXIsEnabled ? this.newMobileUI : this.mainUI }
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/views/mobile/mobile.js